/**
 * DeleteAccountDialog, sheet 43 callout 11 (mission 10 WP16 T096; FR-017, FR-013's screen half, FR-024,
 * NFR-011; D.6, D.8, A.5, UC-04; spec amendment 18; OR-15, U16; style guide §7.1, §7.13, §9;
 * contracts/tenant-lifecycle-api.md "POST /v1/tenant/delete").
 *
 * - `deletionDialogContract` states the D.6 / D.8 contract with its periods from `terms` (`fulfilment_days`,
 *   `class2_years`; FR-022), and `deletionExportFirst` when an export request is open.
 * - The legal name sits in a `navy-50` mono block; `deletionTypedPrompt` labels the input and
 *   `deletionTypedHelper` is the visible reason the danger button stays disabled (tied by
 *   `aria-describedby`) until the typed name equals it exactly: NFC on both, as the server compares, then
 *   `===` with no trim and no case fold.
 * - `deletionPasswordPrompt`: while the password is empty, Delete also stays disabled and states
 *   `deletionPasswordRequired` beneath the field, tied to both by `aria-describedby` (FR-024, style guide §9,
 *   WCAG 2.1 3.3.1; Login.tsx's precedent; WP16 cycle 2).
 * - The 403 `reauthentication-failed` renders `deletionPasswordFailed` in place, under the password, and the
 *   400 naming `legal_name` renders `deletionNameMismatch` under the name: neither signs out, clears the cache
 *   or echoes a typed value (S11); the password is cleared after a refusal. Anything else renders its band.
 * - On the 202, `useDeleteAccountMutation` clears the cache and leaves for `/login?notice=account-closed`.
 * - Non-blocking (OR-15, U16): ×, ghost Cancel and Esc close it, Radix traps focus, and focus returns to the
 *   card's Delete account button (`returnFocus`); closing drops everything typed.
 */
import { useId, useState, type FormEvent, type RefObject } from 'react'
import { CircleAlert, X } from 'lucide-react'

import { Band } from '@/components/trellis/Band'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { Button } from '@/components/ui/button'
import { Dialog, DialogBody, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiError, type Problem } from '@/lib/api'
import { fieldErrors } from '@/lib/auth'
import { PROBLEM } from '@/lib/problems'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { useDeleteAccountMutation } from '@/lib/queries'

export interface DeleteAccountDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The tenant's legal name as Trellis holds it; Delete stays disabled while it is unknown. */
  legalName: string | undefined
  /** `terms.fulfilment_days`. */
  days: number
  /** `terms.class2_years`. */
  years: number
  /** An export request is open (the window read's `export_request`). */
  exportOpen: boolean
  /** The card's Delete account button. */
  returnFocus: RefObject<HTMLElement | null>
}

type Failure = { kind: 'password' } | { kind: 'name' } | { kind: 'problem'; problem: Problem } | { kind: 'generic' }

function failureOf(error: Error | null): Failure | null {
  if (error === null) return null
  if (!(error instanceof ApiError)) return { kind: 'generic' }
  if (error.problem.type === PROBLEM.REAUTHENTICATION_FAILED) return { kind: 'password' }
  if (error.problem.type === PROBLEM.VALIDATION_FAILED && fieldErrors(error).some((e) => e.field === 'legal_name')) return { kind: 'name' }
  return { kind: 'problem', problem: error.problem }
}

/** The typed name matches only exactly (spec amendment 18). */
function exact(typed: string, legalName: string | undefined): boolean {
  return legalName !== undefined && typed.normalize('NFC') === legalName.normalize('NFC')
}

function FieldError({ id, text }: { id: string; text: string }) {
  return (
    <p id={id} className="flex items-center gap-1 text-caption text-[var(--color-error-700)]">
      <CircleAlert aria-hidden className="size-4 shrink-0" />
      {text}
    </p>
  )
}

type FormProps = Omit<DeleteAccountDialogProps, 'open' | 'onOpenChange' | 'returnFocus'> & { contractId: string }

function DeletionForm({ legalName, days, years, exportOpen, contractId }: FormProps) {
  const mutation = useDeleteAccountMutation()
  const [typed, setTyped] = useState('')
  const [password, setPassword] = useState('')
  const nameId = useId()
  const helperId = useId()
  const nameErrorId = useId()
  const passwordId = useId()
  const passwordErrorId = useId()
  const passwordReasonId = useId()

  const matches = exact(typed, legalName)
  const needsPassword = password === ''
  const blocked = !matches || needsPassword
  // Delete's stated reasons: the exact-name helper, and the password's while it is empty (FR-024).
  const reasons = needsPassword ? `${helperId} ${passwordReasonId}` : helperId
  const failure = failureOf(mutation.error)

  function edit(set: (value: string) => void, value: string) {
    if (mutation.isError) mutation.reset()
    set(value)
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    if (blocked || mutation.isPending) return
    mutation.mutate({ legal_name: typed, password }, { onError: () => setPassword('') })
  }

  return (
    <form onSubmit={submit} noValidate>
      <DialogHeader>
        <DialogTitle>{copyText('deletionTitle')}</DialogTitle>
        <DialogClose asChild>
          <Button variant="ghost" size="icon" aria-label={copyText('confirmationClose')}>
            <X aria-hidden />
          </Button>
        </DialogClose>
      </DialogHeader>
      <DialogBody className="flex flex-col gap-4">
        <p id={contractId}>{PROVISIONAL_COPY.deletionDialogContract.render({ days, years })}</p>
        {exportOpen ? <p>{copyText('deletionExportFirst')}</p> : null}
        {legalName === undefined ? null : <p className="rounded-md bg-navy-50 px-3 py-2 font-mono text-body text-navy-950">{legalName}</p>}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={nameId}>{copyText('deletionTypedPrompt')}</Label>
          <Input
            id={nameId}
            value={typed}
            onChange={(e) => edit(setTyped, e.target.value)}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={failure?.kind === 'name' ? true : undefined}
            aria-describedby={failure?.kind === 'name' ? `${helperId} ${nameErrorId}` : helperId}
          />
          <p id={helperId} className="text-caption text-navy-600">
            {copyText('deletionTypedHelper')}
          </p>
          {failure?.kind === 'name' ? <FieldError id={nameErrorId} text={copyText('deletionNameMismatch')} /> : null}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={passwordId}>{copyText('deletionPasswordPrompt')}</Label>
          <Input
            id={passwordId}
            type="password"
            value={password}
            onChange={(e) => edit(setPassword, e.target.value)}
            autoComplete="current-password"
            aria-invalid={failure?.kind === 'password' ? true : undefined}
            aria-describedby={
              [needsPassword ? passwordReasonId : null, failure?.kind === 'password' ? passwordErrorId : null].filter(Boolean).join(' ') ||
              undefined
            }
          />
          {needsPassword ? (
            <p id={passwordReasonId} className="text-caption text-navy-600">
              {copyText('deletionPasswordRequired')}
            </p>
          ) : null}
          {failure?.kind === 'password' ? <FieldError id={passwordErrorId} text={copyText('deletionPasswordFailed')} /> : null}
        </div>
        {failure?.kind === 'problem' ? <ProblemBand problem={failure.problem} /> : null}
        {failure?.kind === 'generic' ? (
          <Band kind="blocked">
            <p>{copyText('genericError')}</p>
          </Band>
        ) : null}
      </DialogBody>
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="ghost">{copyText('modalCancel')}</Button>
        </DialogClose>
        <Button type="submit" variant="destructive" loading={mutation.isPending} disabled={blocked} aria-describedby={reasons}>
          {copyText('deletionTitle')}
        </Button>
      </DialogFooter>
    </form>
  )
}

export function DeleteAccountDialog({ open, onOpenChange, returnFocus, ...rest }: DeleteAccountDialogProps) {
  const contractId = useId()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={contractId}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          returnFocus.current?.focus()
        }}
      >
        <DeletionForm {...rest} contractId={contractId} />
      </DialogContent>
    </Dialog>
  )
}
